import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import moment from 'moment';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppLoad } from '../../contexts/AppLoadContext';
import cmsService, { extractGigcasterIdFromChannel } from '../../services/CMSService';
import { preloadSlimEventDataForChannels } from '../../utils/channelDateUtils';
import ChannelCard from '../ChannelCard';
import ThumbnailHoverModal from '../ThumbnailHoverModal';
import PDPModal from '../PDPModal';
import LoadingSpinner from '../common/LoadingSpinner';
import WatchButton from '../common/WatchButton';
import { useWatchButtonState } from '../../hooks/useWatchButtonState';
import './EventsPage.css';

// ── Custom-field helpers ─────────────────────────────────────────────

const parseTruthyFlag = value => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const n = value.trim().toLowerCase();
    return ['true', '1', 'yes', 'y'].includes(n);
  }
  return false;
};

const hasCustomFlag = (category, flagName) => {
  const fields = category?.custom_fields;
  if (!fields) return false;

  if (!Array.isArray(fields) && typeof fields === 'object') {
    return parseTruthyFlag(fields[flagName]);
  }

  if (Array.isArray(fields)) {
    const field = fields.find(
      f => (f?.field_title || f?.name || f?.key || '').trim().toLowerCase() === flagName,
    );
    return field ? parseTruthyFlag(field.field_value ?? field.value) : false;
  }

  return false;
};

// ── Countdown Hook (moment-based UTC→local conversion) ───────────────

const useCountdown = targetDate => {
  const calculateTimeLeft = useCallback(() => {
    if (!targetDate) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
    }

    const target = moment.utc(targetDate);
    const now = moment();
    const diff = target.diff(now);

    if (diff <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
    }

    const duration = moment.duration(diff);
    return {
      days: Math.floor(duration.asDays()),
      hours: duration.hours(),
      minutes: duration.minutes(),
      seconds: duration.seconds(),
      expired: false,
    };
  }, [targetDate]);

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    return () => clearInterval(timer);
  }, [calculateTimeLeft]);

  return timeLeft;
};

// ── Countdown Unit ───────────────────────────────────────────────────

const CountdownUnit = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <span className="events-countdown-value">{String(value).padStart(2, '0')}</span>
    <span className="events-countdown-label">{label}</span>
  </div>
);

// ── Hero Section ─────────────────────────────────────────────────────

const FeaturedEventHero = ({ channel, eventData, onWatchNow, onLearnMore }) => {
  const watchState = useWatchButtonState(channel, eventData);

  // Live start_time ?? event_date (from watchState) overrides the cached date.
  const eventDate =
    watchState.effectiveDate || eventData?.event_date || channel?.custom_fields?.event_date;

  // Landscape hero: `spotlight_poster` (CMS "Key Art") is the 2:3 portrait
  // asset, so it sits behind the landscape options here.
  const posterUrl =
    eventData?.poster_image || channel?.poster || channel?.wallpaper || channel?.spotlight_poster;
  const eventTitle = eventData?.name || channel?.title || 'BKFC Event';
  const venueDisplay = eventData?.location || eventData?.venue_name || channel?.location || '';
  const buyTicketsUrl = eventData?.buy_tickets_url;

  const formattedDate = eventDate ? moment.utc(eventDate).local().format('dddd, MMMM D, YYYY') : '';

  const formattedTime = eventDate ? moment.utc(eventDate).local().format('h:mm A') : '';

  let tzAbbr = '';
  if (eventDate) {
    try {
      tzAbbr =
        new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
          .formatToParts(new Date(eventDate))
          .find(p => p.type === 'timeZoneName')?.value || '';
    } catch {
      /* timezone lookup unsupported */
    }
  }

  return (
    <div className="events-hero">
      <div className="events-hero-bg">
        {posterUrl && (
          <img
            src={posterUrl}
            alt={eventTitle}
            className="events-hero-bg-image"
            onError={e => {
              e.target.style.display = 'none';
            }}
          />
        )}
        <div className="events-hero-overlay" />
      </div>

      <div className="events-hero-content">
        <div className="events-hero-badge">
          <span className="events-hero-badge-dot" />
          {watchState.isLive ? 'LIVE NOW' : 'NEXT EVENT'}
        </div>

        <h1 className="events-hero-title">{eventTitle}</h1>

        <div className="events-hero-meta">
          {formattedDate && (
            <div className="events-hero-meta-item">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span>
                {formattedDate} &middot; {formattedTime}
                {tzAbbr ? ` ${tzAbbr}` : ''}
              </span>
            </div>
          )}
          {venueDisplay && (
            <div className="events-hero-meta-item">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <span>{venueDisplay}</span>
            </div>
          )}
        </div>

        {watchState.buttonState === 'watch-in' && watchState.countdown && (
          <div className="events-countdown">
            <span className="events-countdown-prefix">Starts In</span>
            <div className="events-countdown-units">
              {Number(watchState.countdown.days) > 0 && (
                <CountdownUnit value={watchState.countdown.days} label="DAYS" />
              )}
              <CountdownUnit value={watchState.countdown.hours} label="HRS" />
              <CountdownUnit value={watchState.countdown.minutes} label="MIN" />
              <CountdownUnit value={watchState.countdown.seconds} label="SEC" />
            </div>
          </div>
        )}

        {watchState.isLive && (
          <div className="events-hero-live-badge">
            <span className="events-hero-live-dot" />
            LIVE NOW
          </div>
        )}

        <div className="events-hero-actions">
          <WatchButton
            watchState={watchState}
            onClick={() => onWatchNow?.(channel)}
            onWatchIn={() => onLearnMore?.(channel)}
            className="events-hero-btn-primary"
            size="lg"
          />

          <button
            type="button"
            className="events-hero-btn-secondary"
            onClick={() => onLearnMore?.(channel)}
          >
            Learn More
          </button>

          {buyTicketsUrl && (
            <a
              href={buyTicketsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="events-hero-btn-tertiary"
            >
              Buy Tickets
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Section Header ───────────────────────────────────────────────────

const SectionHeader = ({ title, icon }) => (
  <div className="events-section-header">
    <div className="events-section-header__left">
      {icon}
      <h2 className="events-section-header__title">{title}</h2>
    </div>
  </div>
);

// ── Channel Grid with hover modal support ────────────────────────────

const EventChannelGrid = ({ channels }) => {
  const gridRef = useRef(null);
  const hideTimeoutRef = useRef(null);

  const [hoverModal, setHoverModal] = useState({
    isVisible: false,
    channel: null,
    position: null,
    index: -1,
  });

  const [pdpModal, setPdpModal] = useState({
    isVisible: false,
    channel: null,
    videoState: { autoPlay: false, currentTime: 0 },
  });

  const handleHoverModalShow = useCallback((channel, position, index) => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    const gridRect = gridRef.current?.getBoundingClientRect();
    const adjustedPosition = gridRect
      ? { x: position.x - gridRect.left, y: position.y - gridRect.top }
      : position;
    setHoverModal({ isVisible: true, channel, position: adjustedPosition, index });
  }, []);

  const handleHoverModalHide = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    hideTimeoutRef.current = setTimeout(() => {
      setHoverModal({ isVisible: false, channel: null, position: null, index: -1 });
      hideTimeoutRef.current = null;
    }, 300);
  }, []);

  const handleHoverModalHideImmediate = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setHoverModal({ isVisible: false, channel: null, position: null, index: -1 });
  }, []);

  const handleModalMouseEnter = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const handleModalMouseLeave = useCallback(() => {
    handleHoverModalHide();
  }, [handleHoverModalHide]);

  const handleOpenPDPModal = useCallback(
    videoState => {
      handleHoverModalHideImmediate();
      setPdpModal({
        isVisible: true,
        channel: hoverModal.channel,
        videoState: videoState || { autoPlay: false, currentTime: 0 },
      });
    },
    [hoverModal.channel, handleHoverModalHideImmediate],
  );

  const handleClosePDPModal = useCallback(() => {
    setPdpModal({
      isVisible: false,
      channel: null,
      videoState: { autoPlay: false, currentTime: 0 },
    });
  }, []);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  return (
    <div className="events-grid relative" ref={gridRef}>
      {channels.map((ch, index) => (
        <div key={ch._id || index} className="events-grid__item">
          <ChannelCard
            channel={ch}
            index={index}
            onHoverModalShow={handleHoverModalShow}
            onHoverModalHide={handleHoverModalHide}
            onCardClick={handleHoverModalHideImmediate}
          />
        </div>
      ))}

      {hoverModal.isVisible && hoverModal.channel && (
        <ThumbnailHoverModal
          channel={hoverModal.channel}
          isVisible={true}
          position={hoverModal.position}
          onMouseEnter={handleModalMouseEnter}
          onMouseLeave={handleModalMouseLeave}
          onOpenPDPModal={handleOpenPDPModal}
        />
      )}

      {pdpModal.isVisible && (
        <PDPModal
          channel={pdpModal.channel}
          isVisible={pdpModal.isVisible}
          onClose={handleClosePDPModal}
          autoPlayVideo={pdpModal.videoState.autoPlay}
          videoCurrentTime={pdpModal.videoState.currentTime}
        />
      )}
    </div>
  );
};

// ── Main EventsPage ──────────────────────────────────────────────────

const EventsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { categories, loading: appLoading } = useAppLoad();
  const [heroChannel, setHeroChannel] = useState(null);
  const [heroEventData, setHeroEventData] = useState(null);
  const [dataResolved, setDataResolved] = useState(false);
  const [heroPdpModal, setHeroPdpModal] = useState({ isVisible: false, channel: null });
  const [, setCmsHydrationTick] = useState(0);

  const upcomingChannels = useMemo(() => {
    if (!categories?.length) return [];
    const matched = categories.filter(cat => hasCustomFlag(cat, 'is_upcoming_events'));
    return matched.flatMap(cat => cat.channels || []);
  }, [categories]);

  const pastChannels = useMemo(() => {
    if (!categories?.length) return [];
    const matched = categories.filter(cat => hasCustomFlag(cat, 'is_past_events'));
    return matched.flatMap(cat => cat.channels || []);
  }, [categories]);

  useEffect(() => {
    let isMounted = true;

    const preloadSlimEventsForEventCards = async () => {
      const allChannels = [...upcomingChannels, ...pastChannels];
      if (allChannels.length === 0) return;
      const preloadedCount = await preloadSlimEventDataForChannels(allChannels);
      if (isMounted && preloadedCount > 0) setCmsHydrationTick(v => v + 1);
    };

    preloadSlimEventsForEventCards();

    return () => {
      isMounted = false;
    };
  }, [upcomingChannels, pastChannels]);

  // Resolve hero: pick the soonest future event based on CMS dates
  useEffect(() => {
    if (upcomingChannels.length === 0 && pastChannels.length === 0) {
      setDataResolved(true);
      return;
    }

    let isMounted = true;

    const resolve = async () => {
      try {
        if (upcomingChannels.length === 0) {
          setDataResolved(true);
          return;
        }

        // Resolve gigcaster IDs and fetch event data for all upcoming channels
        const channelEvents = await Promise.all(
          upcomingChannels.map(async ch => {
            let gigcasterId = extractGigcasterIdFromChannel(ch);
            if (!gigcasterId) {
              const resolvedIds = await cmsService.resolveGigcasterIdsForChannel(ch);
              gigcasterId = resolvedIds?.[0] || null;
            }
            if (!gigcasterId) return { channel: ch, event: null, date: null };

            const eventObj = await cmsService.getEventByGigcasterId(gigcasterId);
            // Live start_time ?? event_date overrides the cached date so the
            // "soonest upcoming" hero selection respects last-minute reschedules.
            const live = await cmsService.getLiveStatus(gigcasterId).catch(() => null);
            const dateStr = live?.start_time || live?.event_date || eventObj?.event_date || null;
            const date = dateStr ? new Date(dateStr) : null;
            return { channel: ch, event: eventObj, date };
          }),
        );

        if (!isMounted) return;

        // Find the soonest future event
        const now = new Date();
        const futureEvents = channelEvents
          .filter(item => item.date && item.date > now)
          .sort((a, b) => a.date - b.date);

        const hero = futureEvents[0] || channelEvents[0];

        if (hero) {
          setHeroChannel(hero.channel);
          if (hero.event) setHeroEventData(hero.event);
        }
      } catch (err) {
        console.error('[EventsPage] Failed to resolve hero event data:', err);
      } finally {
        if (isMounted) setDataResolved(true);
      }
    };

    resolve();

    return () => {
      isMounted = false;
    };
  }, [upcomingChannels, pastChannels]);

  const handleHeroWatchNow = useCallback(
    channel => {
      if (!channel) return;

      if (channel.subscription_access?.unlocked === false) {
        const channelId = channel.dspro_id || channel._id;
        if (channelId) {
          navigate(`/checkout/${channelId}`, { state: { channel } });
        }
        return;
      }

      const channelSlug = channel.slug || channel.permalink || channel._id || channel.id;
      if (!channelSlug) return;

      navigate(`/watch/${channelSlug}`, {
        state: { from: location.pathname, channel },
      });
    },
    [navigate, location.pathname],
  );

  const handleHeroLearnMore = useCallback(channel => {
    if (!channel) return;
    setHeroPdpModal({ isVisible: true, channel });
  }, []);

  const handleCloseHeroPdpModal = useCallback(() => {
    setHeroPdpModal({ isVisible: false, channel: null });
  }, []);

  if (appLoading && !categories?.length) {
    return <LoadingSpinner message="Loading events..." />;
  }

  const hasNoEvents = upcomingChannels.length === 0 && pastChannels.length === 0;

  return (
    <div className="min-h-screen bg-primary text-white">
      {heroChannel && (
        <FeaturedEventHero
          channel={heroChannel}
          eventData={heroEventData}
          onWatchNow={handleHeroWatchNow}
          onLearnMore={handleHeroLearnMore}
        />
      )}

      <div className="container mx-auto pb-16 events-content">
        {upcomingChannels.length > 0 && (
          <section className="events-section">
            <SectionHeader
              title="Upcoming Events"
              icon={
                <svg
                  className="events-section-header__icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              }
            />
            <EventChannelGrid channels={upcomingChannels} />
          </section>
        )}

        {pastChannels.length > 0 && (
          <section className="events-section">
            <SectionHeader
              title="Past Events"
              icon={
                <svg
                  className="events-section-header__icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              }
            />
            <EventChannelGrid channels={pastChannels} />
          </section>
        )}

        {!appLoading && dataResolved && hasNoEvents && (
          <div className="text-center py-20">
            <svg
              className="w-16 h-16 text-text-muted mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <p className="text-text-muted text-lg font-body">No events available at this time.</p>
            <p className="text-text-muted text-sm font-body mt-2">
              Check back soon for new fight announcements.
            </p>
          </div>
        )}
      </div>

      {heroPdpModal.isVisible && heroPdpModal.channel && (
        <PDPModal
          channel={heroPdpModal.channel}
          isVisible={heroPdpModal.isVisible}
          onClose={handleCloseHeroPdpModal}
        />
      )}
    </div>
  );
};

export default EventsPage;
